import {
  AutoPolicy,
  PupPolicy,
  PropertyPolicy,
  Vehicle,
} from '@aaa-ncnu-ie/mypolicy-api-policies';
import { Box, Card, Stack, Typography } from '@mui/material';
import React from 'react';
import { NumericFormat } from 'react-number-format';
import { useNavigate } from 'react-router-dom';

import CardAlert from '../../components/CardAlert';
import { DocumentChip } from '../../components/DocumentChip';
import InfoBlock from '../../components/InfoBlock';
import StatusChip from '../../components/StatusChip';
import Button from '../../components/styledComponents/Button';
import { ContainedLink } from '../../components/styledComponents/Link';
import PhoneNumberLink from '../../components/styledComponents/PhoneNumberLink';
import colors from '../../constants/colors';
import useAnalytics from '../../hooks/useAnalytics';
import { isNextGenPolicy } from '../../utils/nextGenUtils';
import {
  policyTypeMap,
  policyTypeMapSelector,
} from '../../utils/policyTypeUtil';

interface PolicyDetailsCardProps {
  policy: AutoPolicy | PropertyPolicy | PupPolicy;
  isNanoPolicy?: boolean;
}

const PolicyDetailsCard = ({
  policy,
  isNanoPolicy,
}: PolicyDetailsCardProps) => {
  const { policySummary } = policy;
  const isAutoPolicy = policySummary.policyTypeCd === 'PA';
  const isHomePolicy = policySummary.policyTypeCd === 'HO';
  const policyCancelled = policySummary.policyStatus
    .toLowerCase()
    .includes('cancel');
  const policyExpired = policySummary.policyStatus === 'Expired';
  const showPolicyPeriod =
    policySummary.effectiveDate && policySummary.expirationDate;
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const isNYPolicy = isAutoPolicy && policySummary?.riskState === 'NY';
  // PBE (edit/quote policy changes) is not available for NextGen policies in R1,
  // so those entry points are suppressed the same way a non-editable policy's are.
  const showEditPolicyActions =
    !!policySummary.policyEditable &&
    !isNextGenPolicy(policySummary.dataSource);

  const mailingAddressStr = React.useMemo(() => {
    if (policySummary.contactInfo?.mailingAddress) {
      const { addressLine1, addressLine2, city, postalCode, stateProvCd } =
        policySummary.contactInfo.mailingAddress;
      return `${addressLine1}${
        addressLine2 ? ` ${addressLine2}` : ''
      }, ${city}, ${stateProvCd} ${postalCode}`;
    }
    return '';
  }, [policySummary]);

  const propertyAddressStr = React.useMemo(() => {
    if (policySummary.policyDetails?.insuredAddress) {
      const { addressLine1, addressLine2, city, postalCode, stateProvCd } =
        policySummary.policyDetails.insuredAddress;
      let addressLine = addressLine1;
      if (addressLine2) {
        addressLine += `, ${addressLine2}`;
      }
      return `${addressLine}, ${city}, ${stateProvCd} ${postalCode}`;
    }
    return '';
  }, [policySummary]);

  const commonTextStyles = {
    fontFamily: 'Cabin',
    fontKerning: 'normal',
    fontSize: { mobile: '16px', tablet: '18px' },
    lineHeight: '24px',
  };

  const getNanoOrVehicleContent = (vehicles?: Vehicle[]) => (
    <>
      <Typography
        variant="h3"
        sx={{ ...commonTextStyles, fontWeight: 700, marginBottom: '4px' }}
      >
        Vehicles
      </Typography>
      {vehicles?.map(({ year, make, series }) => (
        <Typography
          className="csaa-qm-encrypt"
          key={`${year} ${make} ${series}`}
          sx={{ ...commonTextStyles, color: colors.csaaMid }}
        >
          {`${year} ${make} ${series}`}
        </Typography>
      ))}
    </>
  );

  const policyIconIndex = policyTypeMapSelector(
    policySummary.policyTypeCd,
    policySummary.typeOfPolicy
  );
  const PolicyIcon = policyTypeMap[policyIconIndex].icon;

  const handleViewDocumentsClick = () => {
    track('Document Library Accessed', {
      event_type: 'Link Accessed',
      location: 'Feature Card',
      link: 'View documents',
      policy_number: policySummary.policyNumber,
    });
    navigate(`/documents/${policySummary.policyNumber}`);
  };

  const PolicyButtons = () => {
    if (isAutoPolicy) {
      return (
        <Stack
          direction={{ mobile: 'column', desktop: 'row' }}
          spacing={2}
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <Stack
            direction={{ mobile: 'column', tablet: 'row' }}
            spacing={2}
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <ContainedLink
              to={`/documents/${policySummary.policyNumber}`}
              data-testid="viewDocumentsButton"
              variantColor="white"
              onClick={handleViewDocumentsClick}
            >
              View documents
            </ContainedLink>
            {showEditPolicyActions && (
              <ContainedLink
                to={`/edit/${policySummary.policyNumber}`}
                data-testid="quote-policy-changes-link"
                id="quote-policy-changes-link"
                variantColor="white"
                onClick={() => {
                  track('Edit Policy Accessed', {
                    event_type: 'Link Accessed',
                    location: 'Feature Card',
                    link: 'Quote policy changes',
                    policy_number: policySummary.policyNumber,
                  });
                }}
              >
                Quote policy changes
              </ContainedLink>
            )}
          </Stack>
          {showEditPolicyActions && (
            <Stack
              direction={{ mobile: 'column', tablet: 'row' }}
              sx={{
                justifyContent: 'flex-end',
              }}
            >
              <ContainedLink
                to={`/edit/${policySummary.policyNumber}`}
                data-testid="viewDocumentsButton"
                variantColor="blue"
                onClick={() => {
                  track('Edit Policy Accessed', {
                    event_type: 'Link Accessed',
                    location: 'Feature Card',
                    link: 'Edit policy',
                    policy_number: policySummary.policyNumber,
                  });
                }}
                sx={{
                  whiteSpace: 'nowrap',
                }}
              >
                Edit policy
              </ContainedLink>
            </Stack>
          )}
        </Stack>
      );
    } else if (policySummary.policyTypeCd === 'HO') {
      return (
        <Stack
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <Button
            data-testid="viewDocumentsButton"
            variant="outlined"
            sx={{
              borderWidth: 2,
              borderColor: colors.csaaBlack,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: 18,
              color: colors.csaaBlack,
              height: '100%',
              lineHeight: '24px',
            }}
            onClick={handleViewDocumentsClick}
          >
            View documents
          </Button>
        </Stack>
      );
    } else {
      return null; // no buttons for PUP policies
    }
  };

  const renderPupCoverageLimit = () => {
    if ('pupCoverages' in policy) {
      return (
        <Box>
          {policy.pupCoverages?.coverageDescription && (
            <Typography
              sx={{ ...commonTextStyles, fontWeight: 700, marginBottom: '4px' }}
            >
              {policy.pupCoverages?.coverageDescription}
            </Typography>
          )}
          <Typography sx={{ ...commonTextStyles, color: colors.csaaMid }}>
            {policy.pupCoverages?.coverageLimit.startsWith('$')
              ? policy.pupCoverages?.coverageLimit
              : `$${policy.pupCoverages?.coverageLimit}`}
          </Typography>
          <DocumentChip
            labelText="Policy declarations"
            sx={{ display: { mobile: 'none', tablet: 'inline-flex' } }}
            policyNumber={policy.policySummary?.policyNumber}
            policyType={policy.policySummary?.policyType}
            dataSource={policy.policySummary?.dataSource}
            onClick={() => {
              track('Document Downloaded', {
                event_type: 'File Downloaded',
                document_name: 'Policy Declaration',
                location: 'Feature Card',
                link: 'Policy Declaration',
                policy_number: policySummary.policyNumber,
              });
            }}
          />
        </Box>
      );
    }

    return null;
  };

  const namedInsured: React.ReactElement<any>[] = policySummary.insureds.map(
    (insured) => {
      let nameOfInsured = '';
      if ('fullName' in insured) {
        nameOfInsured = `${insured.fullName}`;
      } else {
        nameOfInsured = `${insured.firstName} ${insured.lastName}`;
      }
      return (
        <Typography
          className="csaa-qm-encrypt"
          key={`${nameOfInsured}`}
          sx={{ ...commonTextStyles, color: colors.csaaMid }}
        >
          {nameOfInsured}
        </Typography>
      );
    }
  );

  return (
    <Card
      sx={{
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <Box
        sx={{
          padding: { mobile: '16px', tablet: '24px 40px' },
          backgroundColor: colors.csaaBlue05,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
        data-testid="PolicyCardHeader"
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
          }}
        >
          <PolicyIcon
            id={policySummary.policyNumber}
            sx={{
              width: { mobile: 32, tablet: 48 },
              height: { mobile: 32, tablet: 48 },
              marginRight: { mobile: 1, tablet: 2 },
            }}
          />
          <Box>
            <Typography
              variant="h2"
              sx={{
                ...commonTextStyles,
                fontWeight: 700,
                marginBottom: '4px',
              }}
            >
              {policySummary.policyNumber}
            </Typography>
            {showPolicyPeriod && (
              <Stack
                direction="row"
                sx={{
                  flexWrap: 'wrap',
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  lineHeight: '24px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { mobile: 14, tablet: 16 },
                    marginRight: 1,
                    fontWeight: 500,
                  }}
                >
                  Policy period
                </Typography>
                <Typography
                  sx={{ fontSize: { mobile: 14, tablet: 16 }, fontWeight: 400 }}
                >
                  {policySummary.effectiveDate} - {policySummary.expirationDate}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>

        {policyCancelled && <CardAlert value="Cancelled" />}
        {policyExpired && <CardAlert value="Expired" />}
        {!policyCancelled && !policyExpired && isNanoPolicy && (
          <StatusChip label="Named Non-owner" />
        )}
      </Box>
      <Box
        sx={{
          px: { mobile: 2, tablet: 5 },
          mt: { mobile: 2, tablet: 5 },
          mb: { mobile: 0, tablet: isNYPolicy ? 0 : 3 },
          display: 'grid',
          gridTemplateColumns: {
            mobile: '1fr',
            tablet: 'repeat(2, minmax(0, 1fr))',
          },
          gridGap: { mobile: '16px', tablet: '24px' },
        }}
        data-testid="policyDetailContainer"
      >
        <Box>
          <Typography
            variant="h3"
            sx={{ ...commonTextStyles, fontWeight: 700, marginBottom: '4px' }}
          >
            Named insured(s)
          </Typography>
          {namedInsured}
        </Box>

        <Box>
          <Typography
            variant="h3"
            sx={{ ...commonTextStyles, fontWeight: 700, marginBottom: '4px' }}
          >
            {isAutoPolicy ? 'Mailing address' : 'Property address'}
          </Typography>
          <Typography
            className="csaa-qm-encrypt"
            sx={{ ...commonTextStyles, color: colors.csaaMid }}
          >
            {isAutoPolicy ? mailingAddressStr : propertyAddressStr}
          </Typography>
        </Box>
        {isAutoPolicy && (
          <Box
            sx={{
              display: {
                mobile: isNanoPolicy ? 'none' : 'block',
                tablet: 'block',
              },
            }}
          >
            {!isNanoPolicy
              ? getNanoOrVehicleContent(policySummary.policyDetails.vehicles)
              : null}
            <DocumentChip
              labelText="ID cards"
              policyNumber={policy.policySummary?.policyNumber}
              policyType={policy.policySummary?.policyType}
              dataSource={policy.policySummary?.dataSource}
              sx={{
                display: { mobile: 'none', tablet: 'inline-flex' },
                width: '94px',
                mb: { mobile: isNYPolicy ? 0 : 2, tablet: isNYPolicy ? 3 : 0 },
              }}
              onClick={() => {
                track('Document Downloaded', {
                  event_type: 'File Downloaded',
                  document_name: 'Identification Card',
                  location: 'Feature Card',
                  link: 'Identification Card',
                  policy_number: policySummary.policyNumber,
                });
              }}
            />
          </Box>
        )}
        {isHomePolicy && (
          <Box>
            <DocumentChip
              labelText="Policy declarations"
              policyNumber={policy.policySummary?.policyNumber}
              policyType={policy.policySummary?.policyType}
              dataSource={policy.policySummary?.dataSource}
              sx={{
                display: { mobile: 'none', tablet: 'inline-flex' },
                width: '158px',
              }}
              onClick={() => {
                track('Document Downloaded', {
                  event_type: 'File Downloaded',
                  document_name: 'Policy Declaration',
                  location: 'Feature Card',
                  link: 'Policy Declaration',
                  policy_number: policySummary.policyNumber,
                });
              }}
            />
          </Box>
        )}
        {renderPupCoverageLimit()}
        <Box>
          <Typography
            variant="h3"
            sx={{ ...commonTextStyles, fontWeight: 700, marginBottom: '4px' }}
          >
            Charges
          </Typography>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ ...commonTextStyles, color: colors.csaaMid }}>
              Premium:
            </Typography>
            <Typography
              sx={{
                ...commonTextStyles,
                color: colors.csaaMid,
              }}
            >
              <NumericFormat
                value={policySummary.premium}
                displayType="text"
                thousandSeparator
                prefix="$"
                fixedDecimalScale
                decimalScale={2}
              />
            </Typography>
          </Stack>
          <Stack sx={{ mt: 1 }}>
            <Typography
              sx={{
                ...commonTextStyles,
                color: colors.csaaMid,
              }}
            >
              Additional taxes, surcharges, and/or fees may be applied.
            </Typography>
          </Stack>
          {isAutoPolicy ? (
            <DocumentChip
              labelText="ID cards"
              sx={{
                display: { mobile: 'inline-flex', tablet: 'none' },
                width: '94px',
              }}
              policyNumber={policy.policySummary?.policyNumber}
              policyType={policy.policySummary?.policyType}
              dataSource={policy.policySummary?.dataSource}
              onClick={() => {
                track('Document Downloaded', {
                  event_type: 'File Downloaded',
                  document_name: 'Identification Card',
                  location: 'Feature Card',
                  link: 'Identification Card',
                  policy_number: policySummary.policyNumber,
                });
              }}
            />
          ) : (
            <DocumentChip
              labelText="Policy declarations"
              sx={{
                display: { mobile: 'inline-flex', tablet: 'none' },
                width: '158px',
              }}
              policyNumber={policy.policySummary?.policyNumber}
              policyType={policy.policySummary?.policyType}
              dataSource={policy.policySummary?.dataSource}
              onClick={() => {
                track('Document Downloaded', {
                  event_type: 'File Downloaded',
                  document_name: 'Policy Declaration',
                  location: 'Feature Card',
                  link: 'Policy Declaration',
                  policy_number: policySummary.policyNumber,
                });
              }}
            />
          )}
        </Box>
      </Box>
      <Stack
        direction="column"
        sx={{
          px: { mobile: 2, tablet: 5 },
          pb: 3,
        }}
      >
        {isNYPolicy && (
          <Stack data-testid="idCardAvailabilityInfoBar" sx={{ mb: 3 }}>
            <InfoBlock
              indent
              text={
                <Stack>
                  <Typography
                    sx={{
                      fontSize: { mobile: 16, tablet: 18 },
                      fontWeight: 400,
                      color: colors.csaaMid,
                      lineHeight: '24px',
                      fontFamily: 'Cabin',
                      fontKerning: 'normal',
                    }}
                  >
                    Insurance ID cards for your state may not be available
                    online. To get ID cards, call Service at{' '}
                    <PhoneNumberLink
                      phoneNumber="18889805650"
                      formattedPhoneNumber="888.980.5650"
                    />
                  </Typography>
                </Stack>
              }
            />
          </Stack>
        )}
        {!showEditPolicyActions && (
          <Stack sx={{ mb: { mobile: 3, tablet: 3 } }}>
            <InfoBlock
              indent
              text={
                <Stack
                  direction={{ mobile: 'column', tablet: 'row' }}
                  spacing={0.5}
                  sx={{
                    alignItems: { mobile: 'left', tablet: 'center' },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { mobile: 16, tablet: 18 },
                      fontFamily: 'Cabin',
                      lineHeight: '24px',
                      fontWeight: 400,
                      letterSpacing: 0.4,
                      color: colors.csaaMid,
                    }}
                  >
                    To make changes to your policy, call{' '}
                    <PhoneNumberLink
                      phoneNumber="+18889805650"
                      formattedPhoneNumber="888.980.5650"
                    />
                  </Typography>
                </Stack>
              }
            />
          </Stack>
        )}
        <PolicyButtons />
      </Stack>
    </Card>
  );
};

export default PolicyDetailsCard;
